import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const NAV = [
  { label: "Research", id: "research" },
  { label: "Topics", id: "topics" },
  { label: "Atlas", id: "atlas", gold: true },
  { label: "Writings", id: "writings" },
  { label: "About", id: "about" },
];

export const Navbar = ({ settings }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const social = settings?.socialLinks || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="section-shell flex items-center justify-between py-4">
        <button
          onClick={() => go("hero")}
          data-testid="nav-logo"
          className="group flex items-center gap-3 text-left"
        >
          <span className="relative flex h-8 w-8 items-center justify-center">
            <span className="absolute inset-0 rounded-full border border-plasma-teal/50 animate-pulse-glow" />
            <span className="h-1.5 w-1.5 rounded-full bg-plasma-teal shadow-[0_0_12px_#1DE9B6]" />
          </span>
          <span className="font-display text-lg leading-none tracking-tight text-white">
            Resonant Spark
            <span className="block font-mono text-[9px] tracking-[0.3em] uppercase text-plasma-teal/70">
              Project
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              className={`relative font-sans text-sm tracking-wide transition-colors duration-300 ${
                n.gold
                  ? "text-gold hover:text-white"
                  : "text-slate-300 hover:text-plasma-teal"
              }`}
            >
              {n.label}
              {n.gold && (
                <span className="absolute -right-3 -top-1 h-1 w-1 rounded-full bg-gold animate-pulse-glow" />
              )}
            </button>
          ))}
          <div className="mx-1 h-4 w-px bg-white/15" />
          <a href={social.substack} target="_blank" rel="noreferrer" data-testid="nav-substack" className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-plasma-teal">Substack</a>
          <a href={social.x} target="_blank" rel="noreferrer" data-testid="nav-x" className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-plasma-teal">X</a>
          <button onClick={() => go("contact")} data-testid="nav-contact" className="btn-plasma !py-2 !px-4 text-xs">
            Collaborate <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="glass overflow-hidden md:hidden"
            data-testid="mobile-menu"
          >
            <div className="section-shell flex flex-col gap-4 py-6">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className={`text-left font-display text-2xl ${n.gold ? "text-gold" : "text-slate-200"}`}
                >
                  {n.label}
                </button>
              ))}
              <div className="mt-2 flex gap-6 border-t border-white/10 pt-4">
                <a href={social.substack} target="_blank" rel="noreferrer" className="font-mono text-xs uppercase tracking-widest text-plasma-teal">Substack</a>
                <a href={social.x} target="_blank" rel="noreferrer" className="font-mono text-xs uppercase tracking-widest text-plasma-teal">X</a>
                <button onClick={() => go("contact")} className="font-mono text-xs uppercase tracking-widest text-gold">Collaborate</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
